import axios from 'axios';
import store from '@/store';
import constants from '@/constants';
import { COLOR } from '@/scripts/colorTheme.js';
import { getToken, deleteToken } from '@/scripts/token.js';
import { refreshUser } from '@/api/user.js';
import router from '@/router';

export const METHOD = {
    GET: 'GET',
    POST: 'POST',
    PATCH: 'PATCH',
    PUT: 'PUT',
    DELETE: 'DELETE',
};

function shouldSkipInterceptor(url) {
    if (!url) return false;

    const skipEndpoints = [
        '/api-v2/auth/refresh',
        '/api-v2/auth/login',
        '/api-v2/auth/register',
        '/api-v2/captcha/generate',
    ];

    return skipEndpoints.some((endpoint) => url.includes(endpoint));
}

// Singleton promise for token refresh — collapses concurrent 401 responses into
// a single refreshUser() call. Without this, multi-tab scenarios (e.g. 25 tabs all
// returning 401 simultaneously after a session expiry) flood /auth/refresh.
let _refreshPromise = null;

// Setup axios response interceptor for automatic token refresh
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Early return for auth and captcha requests to prevent infinite loops
        if (shouldSkipInterceptor(originalRequest.url)) {
            return Promise.reject(error);
        }

        // Early return if already retried or server error
        if (
            originalRequest._retry ||
            !error.response?.status ||
            error.response.status >= 500
        ) {
            return Promise.reject(error);
        }

        // Attempt token refresh and retry
        originalRequest._retry = true;

        try {
            if (!_refreshPromise) {
                _refreshPromise = refreshUser().finally(() => {
                    _refreshPromise = null;
                });
            }
            await _refreshPromise;
            const newToken = await getToken();

            originalRequest.headers['Authorization'] = newToken;
            return axios(originalRequest);
        } catch (refreshError) {
            return null; // Prevent error propagation
        }
    }
);

export async function calculatePromise(objects) {
    let promiseList = [];

    for (let index = 0; index < objects.length; index++) {
        const object = objects[index];
        const promises = await store.dispatch(object.dispatch);

        if (object.isArray === true) {
            for (let indexTwo = 0; indexTwo < promises.length; indexTwo++) {
                const promise = promises[indexTwo];
                promiseList.push({
                    promise: promise,
                    commit: object.commit,
                });
            }
        } else if (promises != null) {
            promiseList.push({
                promise: promises,
                commit: object.commit,
            });
        }
    }

    return promiseList;
}

export async function promiseAll(promiseList) {
    if (promiseList.length === 0) return;

    await Promise.all(
        promiseList.map((promise) => axios(promise.promise))
    ).then(
        axios.spread((...allData) => {
            allData.forEach(async (data, dataIndex) => {
                // Skip null responses (e.g., from authentication failures)
                if (data && data.data) {
                    store.commit(promiseList[dataIndex].commit, data.data);
                }
            });
        })
    );
}

function getContentTypeHeader(isFile, isFormData, data) {
    if (!data) return {};

    if (isFile) {
        return {
            'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
        };
    }

    if (isFormData) {
        return { 'Content-Type': 'application/x-www-form-urlencoded' };
    }

    return { 'Content-Type': 'application/json; charset=UTF-8' };
}

function shouldShowErrorPopup(error) {
    const status = error.response?.status;
    return status !== 401 && status !== 403;
}

export async function axiosRequest({
    url,
    method,
    requestName,
    data = null,
    params = null,
    isLoader = true,
    isFile = false,
    isShowPopup = false,
    isFormData = false,
    customHeaders = null,
    signal = null,
    skipAuth = false,
    skipErrorPopup = false,
    throwOnError = false,
    responseType = null,
    returnFullResponse = false,
}) {
    if (isLoader) store.commit('IS_LOAD', true);

    const token = !skipAuth ? await getToken() : null;
    if (!skipAuth && !token) return null;

    // Prepend backend URL if not already a full URL
    const fullUrl = url.startsWith('http')
        ? url
        : `${process.env.VUE_APP_BACKEND_URL}${url}`;

    const headers = {
        ...(token ? { Authorization: token } : {}),
        'Cache-Control': 'no-cache',
        ...getContentTypeHeader(isFile, isFormData, data),
        ...(customHeaders || {}),
    };

    try {
        const response = await axios({
            method,
            url: fullUrl,
            params,
            paramsSerializer: { indexes: null },
            data,
            headers,
            signal,
            responseType: responseType || undefined,
        });

        if (isShowPopup) {
            store.commit('POPUP_ADD', {
                title: requestName,
                description: ['Success'],
                color: COLOR.GREEN,
            });
        }

        if (returnFullResponse) {
            return response;
        }

        return response.data ? response.data : true;
    } catch (error) {
        if (!skipErrorPopup && shouldShowErrorPopup(error)) {
            store.commit('POPUP_ADD', {
                title: requestName,
                description: [
                    `Status code: ${error.response?.status}`,
                    error.response?.data?.detail,
                ],
                color: COLOR.RED,
                isLongPopup: true,
            });
        }

        if (throwOnError) {
            throw error;
        }

        return null;
    } finally {
        if (isLoader) store.commit('IS_LOAD', false);
    }
}
